import React, { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
// Core Components & Pages
import { AuthProvider } from "./context/AuthContext";
import { DeployAuthProvider } from "./context/DeployAuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsTracker from "./components/AnalyticsTracker";
import ToolLayout from "./components/openSource/ToolLayout";
import LoadingScreen from "./components/LoadingScreen";
import ProtectedRoute from "./components/ProtectedRoute";
import Home from "./pages/Home";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import Services from "./pages/Services";
import Solutions from "./pages/Solutions";
import Consulting from "./pages/Consulting";
import BusinessWebsites from "./pages/BusinessWebsites";
import EcommerceComingSoon from "./pages/EcommerceComingSoon";
import CustomDevelopment from "./pages/CustomDevelopment";
import BookingWebsites from "./pages/BookingWebsites";
import OpenSource from "./pages/OpenSource";
import ElectronicsToolkit from "./pages/openSource/tools/ElectronicsToolkit";
import BalloonGenerator from "./pages/openSource/tools/BalloonGenerator";
import STM32SuiteStandalone from "./pages/openSource/libraries/stm32/STM32SuiteStandalone";
import LoginPage from "./pages/Login";
import DeveloperPage from "./developer/pages/DeveloperPage";
import AnalyticsPage from "./developer/pages/AnalyticsPage";
import MessagesPage from "./developer/pages/MessagesPage";
import ResumesPage from "./developer/pages/ResumesPage";
import BlogPage from "./developer/pages/BlogPage";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ConsentBanner from "./components/ConsentBanner";
import ChatWidget from "./components/chat/ChatWidget";
import MessageDetailView from './developer/components/MessageDetailView';
import { clearLegacyDeployMcpToken } from "./utils/deployMcpTokenStorage";

// Legal Pages
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import CookiePolicy from "./pages/legal/CookiePolicy";
import EULA from "./pages/legal/EULA";
import DeployLandingPage from "./pages/deploy/DeployLandingPage";
import DeployLoginPage from "./pages/deploy/DeployLoginPage";
import DeployForgotPasswordPage from "./pages/deploy/DeployForgotPasswordPage";
import DeployResetPasswordPage from "./pages/deploy/DeployResetPasswordPage";
import DeployTermsPage from "./pages/deploy/DeployTermsPage";
import DeployLimitationsPage from "./pages/deploy/DeployLimitationsPage";
import DeployAbuseReportPage from "./pages/deploy/DeployAbuseReportPage";
import DeployDashboardPage from "./pages/deploy/DeployDashboardPage";
import DeployProtectedRoute from "./components/deploy/DeployProtectedRoute";
import DeployMaintenanceBannerStrip from "./components/deploy/DeployMaintenanceBannerStrip";
import LimeliteLandingPage from "./pages/limelite/LimeliteLandingPage";
import LimelitePilotPage from "./pages/limelite/LimelitePilotPage";
import LimeliteProjectLibraryPage from "./pages/limelite/LimeliteProjectLibraryPage";
import PilotSupport from "./components/deploy/PilotSupport";
import LimeliteDeploySetupPage from "./pages/limelite/LimeliteDeploySetupPage";
import LimeliteDeployDocsPage from "./pages/limelite/LimeliteDeployDocsPage";
import LimeliteDeployLegalPage from "./pages/limelite/LimeliteDeployLegalPage";
import LimeliteMarketplacePage from "./pages/limelite/LimeliteMarketplacePage";
import LimeliteMarketplaceDetailPage from "./pages/limelite/LimeliteMarketplaceDetailPage";
import StorefrontDashboardPage from "./pages/limelite/storefront/StorefrontDashboardPage";
import StorefrontProvisioningPage from "./pages/limelite/storefront/StorefrontProvisioningPage";
import CreatorProvisioningPage from "./pages/limelite/creator/CreatorProvisioningPage";
import LimeliteAccountPage from "./pages/limelite/LimeliteAccountPage";
import DeployInfrastructurePage from "./pages/deploy/DeployInfrastructurePage"; // RD-0829
import DeployProjectDetailPage from "./pages/deploy/DeployProjectDetailPage";

// Wrapper component to conditionally render footer
const FooterWrapper = () => {
  const location = useLocation();
  const isDeveloperRoute = location.pathname.startsWith('/developer');

  return isDeveloperRoute ? null : <Footer />;
};

function AppContent() {
  const { i18n, ready } = useTranslation();

  useEffect(() => {
    // Show the product on first visit. The header language switcher remains available.
    // Respect an explicit saved preference instead of overwriting it on every visit.
    try {
      const storedLang = localStorage.getItem('selectedLanguage');
      if (['en', 'hr'].includes(storedLang) && i18n.language !== storedLang) i18n.changeLanguage(storedLang);
    } catch { /* Browsing remains available when storage is disabled. */ }
  }, [i18n]);

  // A previous dashboard version stored full rd_* bearer tokens in
  // localStorage. Remove those legacy values on every app boot without ever
  // reading them; newly created keys remain in React memory only.
  useEffect(() => {
    clearLegacyDeployMcpToken();
  }, []);

  if (!ready) {
    return <LoadingScreen />;
  }

  return (
    <div className="App flex flex-col min-h-screen">
      <ScrollToTop />
      <AnalyticsTracker />
      <Navbar />
      <DeployMaintenanceBannerStrip />
      <main className="flex-grow bg-black pt-16">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/consulting" element={<Consulting />} />
          <Route path="/business-websites" element={<BusinessWebsites />} />
          <Route path="/custom-development" element={<CustomDevelopment />} />
          <Route path="/booking-websites" element={<BookingWebsites />} />
          <Route path="/ecommerce-coming-soon" element={<EcommerceComingSoon />} />
          <Route path="/about" element={<About />} />
          <Route path="/open-source" element={<OpenSource />} />
          {/* Each tool is wrapped in ToolLayout so it inherits the same
              breadcrumb, category sidebar and Prev/Next tour without any change
              to its own internals. The id maps to the manifest entry. */}
          <Route path="/open-source/tools/electronics" element={<ToolLayout id="electronics"><ElectronicsToolkit /></ToolLayout>} />
          <Route path="/open-source/solutions/balloon-generator" element={<ToolLayout id="balloon"><BalloonGenerator /></ToolLayout>} />
          <Route path="/open-source/stm32-embedded-c-suite" element={<ToolLayout id="stm32"><STM32SuiteStandalone /></ToolLayout>} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/developer" element={<LoginPage />} />

          {/* Legal routes */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/eula" element={<EULA />} />

          {/* Limelite product hub + sub-pages */}
          <Route path="/limelite" element={<LimeliteLandingPage />} />
          <Route path="/limelite/pilot" element={<LimelitePilotPage />} />
          <Route path="/limelite/projects" element={<LimeliteProjectLibraryPage />} />
          <Route path="/limelite/dashboard" element={<Navigate to="/limelite/deploy/dashboard" replace />} />
          <Route path="/limelite/deploy/setup" element={<LimeliteDeploySetupPage />} />
          <Route path="/limelite/deploy/docs" element={<LimeliteDeployDocsPage />} />
          <Route path="/limelite/deploy/legal" element={<LimeliteDeployLegalPage />} />
          {/* Examples belong under one Limelite product, not separate public funnels. */}
          <Route path="/limelite/ecommerce" element={<Navigate to="/limelite" replace />} />
          <Route path="/limelite/marketplace" element={<LimeliteMarketplacePage />} />
          <Route path="/limelite/shop" element={<LimeliteMarketplacePage />} />
          <Route path="/limelite/marketplace/:slug" element={<LimeliteMarketplaceDetailPage />} />
          {/* Retired public Storefront funnel. Existing customers retain protected
              dashboard/provisioning compatibility routes below. */}
          <Route path="/limelite/storefront" element={<Navigate to="/limelite" replace />} />
          <Route
            path="/limelite/account"
            element={
              <DeployProtectedRoute>
                <LimeliteAccountPage />
              </DeployProtectedRoute>
            }
          />
          <Route
            path="/limelite/storefront/dashboard"
            element={
              <DeployProtectedRoute>
                <StorefrontDashboardPage />
              </DeployProtectedRoute>
            }
          />
          <Route
            path="/limelite/storefront/provisioning"
            element={
              <DeployProtectedRoute>
                <StorefrontProvisioningPage />
              </DeployProtectedRoute>
            }
          />

          {/* Legacy Creator marketing links resolve to the single Limelite platform story.
              Authenticated provisioning remains on its protected compatibility route below. */}
          <Route path="/limelite/creator" element={<Navigate to="/limelite" replace />} />
          <Route path="/limelite/creator/start" element={<Navigate to="/limelite" replace />} />
          <Route
            path="/limelite/creator/provisioning"
            element={
              <DeployProtectedRoute>
                <CreatorProvisioningPage />
              </DeployProtectedRoute>
            }
          />

          {/* Limelite Deploy — canonical routes under /limelite/deploy/* */}
          <Route path="/limelite/deploy" element={<DeployLandingPage />} />
          <Route path="/limelite/deploy/login" element={<DeployLoginPage />} />
          <Route path="/limelite/deploy/forgot-password" element={<DeployForgotPasswordPage />} />
          <Route path="/limelite/deploy/reset-password" element={<DeployResetPasswordPage />} />
          <Route path="/limelite/deploy/terms" element={<DeployTermsPage />} />
          <Route path="/limelite/deploy/limitations" element={<DeployLimitationsPage />} />
          <Route path="/limelite/deploy/abuse-report" element={<DeployAbuseReportPage />} />
          <Route path="/limelite/deploy/account" element={<Navigate to="/limelite/account" replace />} />
          <Route
            path="/limelite/deploy/dashboard"
            element={
              <DeployProtectedRoute>
                <DeployDashboardPage />
              </DeployProtectedRoute>
            }
          />
          <Route
            path="/limelite/deploy/infrastructure"
            element={
              <DeployProtectedRoute>
                <DeployInfrastructurePage />
              </DeployProtectedRoute>
            }
          />
          <Route
            path="/limelite/deploy/projects/:slug"
            element={
              <DeployProtectedRoute>
                <DeployProjectDetailPage />
              </DeployProtectedRoute>
            }
          />

          {/* Legacy /deploy/* redirects — keeps old bookmarks and email links working */}
          <Route path="/deploy" element={<Navigate to="/limelite/deploy" replace />} />
          <Route path="/deploy/login" element={<Navigate to="/limelite/deploy/login" replace />} />
          <Route path="/deploy/forgot-password" element={<Navigate to="/limelite/deploy/forgot-password" replace />} />
          <Route path="/deploy/reset-password" element={<Navigate to="/limelite/deploy/reset-password" replace />} />
          <Route path="/deploy/terms" element={<Navigate to="/limelite/deploy/terms" replace />} />
          <Route path="/deploy/limitations" element={<Navigate to="/limelite/deploy/limitations" replace />} />
          <Route path="/deploy/abuse-report" element={<Navigate to="/limelite/deploy/abuse-report" replace />} />
          <Route path="/deploy/account" element={<Navigate to="/limelite/deploy/account" replace />} />
          <Route path="/deploy/dashboard" element={<Navigate to="/limelite/deploy/dashboard" replace />} />
          <Route path="/deploy/infrastructure" element={<Navigate to="/limelite/deploy/infrastructure" replace />} />

          {/* Protected Routes (Admin/Developer) */}
          <Route element={<ProtectedRoute />}>
            {/* Add Developer Dashboard Route */}
            <Route path="/developer/dashboard" element={<DeveloperPage />} />
            {/* Add Developer Analytics Page Route */}
            <Route path="/developer/analytics" element={<AnalyticsPage />} />
            {/* Add Developer Messages Page Route */}
            <Route path="/developer/messages" element={<MessagesPage />} />
            {/* Updated MessageDetailView Route */}
            <Route
              path="/developer/messages/:messageid"
              element={<MessageDetailView />}
            />
            {/* Add Developer Resumes Page Route */}
            <Route path="/developer/resumes" element={<ResumesPage />} />
            {/* Add Developer Blog Page Route */}
            <Route path="/developer/blog" element={<BlogPage />} />
          </Route>

          {/* Catch-all Not Found Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <FooterWrapper />
      <PilotSupport />
      <ConsentBanner />
      {/* Mounted outside <Routes> on purpose: following a link the assistant gives
          you is a client-side navigation, so the conversation survives it. */}
      <ChatWidget />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <DeployAuthProvider>
          <AppContent />
        </DeployAuthProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
